import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { WorkflowService } from "../../service/workflow.service";
import { CaseStage, CaseTypeOption } from "./s09-workflow.models";

export interface LifecycleRow {
  type: CaseTypeOption;
  stages: CaseStage[];
  chain: string;
  transitions: string;
  status: "ACTIVE" | "DRAFT" | "—";
}

/** Editor row. Moves point at other rows by uid so a rename does not break them. */
interface DraftStage {
  uid: number;
  stage_id?: number;
  stage_name: string;
  movesTo: number[];
}

@Component({
  selector: "app-s09-case-flow",
  templateUrl: "./s09-case-flow.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09CaseFlowComponent implements OnInit, OnChanges {
  @Input() brandKey: number | null = null;
  /** Bumped by the landing "New Case Lifecycle" button. */
  @Input() newToken = 0;
  @Output() countChange = new EventEmitter<number>();
  @Output() editingChange = new EventEmitter<boolean>();

  mode: "list" | "edit" = "list";
  rows: LifecycleRow[] = [];
  loading = false;
  saving = false;
  dirty = false;
  hint = "";

  caseTypeKey: number | null = null;
  stages: DraftStage[] = [];
  selectedUid: number | null = null;
  private nextUid = 1;

  constructor(private workflowService: WorkflowService) {}

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch["brandKey"] && !ch["brandKey"].firstChange) this.loadList();
    if (ch["newToken"] && !ch["newToken"].firstChange && this.newToken > 0) {
      this.startNew();
    }
  }

  private brand(): number {
    return Number(this.brandKey) || 200104;
  }

  loadList(): void {
    this.loading = true;
    this.hint = "";
    this.workflowService.getCaseTypes(this.brand(), true).subscribe((types) => {
      const list = types || [];
      if (!list.length) {
        this.rows = [];
        this.loading = false;
        this.countChange.emit(0);
        this.hint = "No case types for this brand.";
        return;
      }
      forkJoin(
        list.map((t) =>
          this.workflowService.getCaseFlow(this.brand(), t.typeKey).pipe(
            catchError(() => of([] as CaseStage[]))
          )
        )
      ).subscribe((stageSets) => {
        this.rows = list.map((type, i) => this.toRow(type, stageSets[i] || []));
        this.loading = false;
        this.countChange.emit(this.rows.length);
      });
    });
  }

  private toRow(type: CaseTypeOption, stages: CaseStage[]): LifecycleRow {
    const names = stages.map((s) => s.stage_name).filter(Boolean);
    const raw = (type.status || "").toLowerCase();
    const status: LifecycleRow["status"] =
      raw === "published" || raw === "active"
        ? "ACTIVE"
        : raw === "draft"
          ? "DRAFT"
          : names.length
            ? "ACTIVE"
            : "DRAFT";
    const transitions = this.storedTransitionCount(stages);
    return {
      type,
      stages,
      chain: names.length ? names.join(" → ") : "—",
      transitions: transitions > 0 ? String(transitions) : "—",
      status,
    };
  }

  private storedTransitionCount(stages: CaseStage[]): number {
    const named = stages.filter((s) => s.stage_name);
    if (named.some((s) => s.moves_to)) {
      return named.reduce((n, s) => n + (s.moves_to?.length || 0), 0);
    }
    return named.length > 1 ? named.length - 1 : 0;
  }

  open(row: LifecycleRow): void {
    this.caseTypeKey = row.type.typeKey;
    this.stages = this.toDraft(row.stages);
    this.selectedUid = this.stages[0]?.uid ?? null;
    this.dirty = false;
    this.hint = "";
    this.setMode("edit");
  }

  startNew(): void {
    this.caseTypeKey = null;
    const intake = this.blank("Intake");
    const closed = this.blank("Closed");
    intake.movesTo = [closed.uid];
    this.stages = [intake, closed];
    this.selectedUid = intake.uid;
    this.dirty = false;
    this.hint = "";
    this.setMode("edit");
  }

  back(): void {
    this.setMode("list");
    this.hint = "";
    this.loadList();
  }

  private setMode(mode: "list" | "edit"): void {
    this.mode = mode;
    this.editingChange.emit(mode === "edit");
  }

  onTypeChange(raw: number | string | null): void {
    const key = raw == null || raw === "" ? null : Number(raw);
    this.caseTypeKey = key && Number.isFinite(key) ? key : null;
    this.dirty = true;
    this.hint = "";
    if (!this.caseTypeKey) return;
    const existing = this.rows.find((r) => r.type.typeKey === this.caseTypeKey);
    if (existing?.stages.length) {
      this.stages = this.toDraft(existing.stages);
      this.selectedUid = this.stages[0]?.uid ?? null;
    }
  }

  addStage(): void {
    const row = this.blank("");
    this.stages = [...this.stages, row];
    this.selectedUid = row.uid;
    this.dirty = true;
    this.hint = "";
  }

  rename(uid: number, name: string): void {
    this.stages = this.stages.map((s) =>
      s.uid === uid ? { ...s, stage_name: name } : s
    );
    this.dirty = true;
  }

  select(uid: number): void {
    this.selectedUid = uid;
  }

  move(index: number, dir: -1 | 1): void {
    const next = index + dir;
    if (next < 0 || next >= this.stages.length) return;
    const copy = [...this.stages];
    const [row] = copy.splice(index, 1);
    copy.splice(next, 0, row);
    this.stages = copy;
    this.dirty = true;
  }

  remove(index: number): void {
    const gone = this.stages[index];
    if (!gone) return;
    this.stages = this.stages
      .filter((s) => s.uid !== gone.uid)
      .map((s) => ({ ...s, movesTo: s.movesTo.filter((id) => id !== gone.uid) }));
    if (this.selectedUid === gone.uid) {
      this.selectedUid = this.stages[Math.min(index, this.stages.length - 1)]?.uid ?? null;
    }
    this.dirty = true;
  }

  toggleMove(targetUid: number): void {
    const current = this.selected;
    if (!current || current.uid === targetUid) return;
    const has = current.movesTo.includes(targetUid);
    const movesTo = has
      ? current.movesTo.filter((id) => id !== targetUid)
      : [...current.movesTo, targetUid];
    this.stages = this.stages.map((s) =>
      s.uid === current.uid ? { ...s, movesTo } : s
    );
    this.dirty = true;
  }

  canMove(targetUid: number): boolean {
    return !!this.selected?.movesTo.includes(targetUid);
  }

  get selected(): DraftStage | null {
    return this.stages.find((s) => s.uid === this.selectedUid) || null;
  }

  others(): DraftStage[] {
    return this.stages.filter((s) => s.uid !== this.selectedUid);
  }

  isEntry(stage: DraftStage): boolean {
    return this.stages[0]?.uid === stage.uid;
  }

  isTerminal(stage: DraftStage): boolean {
    return stage.movesTo.length === 0;
  }

  moveNames(stage: DraftStage): string[] {
    return stage.movesTo
      .map((id) => this.stages.find((s) => s.uid === id)?.stage_name.trim() || "")
      .filter(Boolean);
  }

  stageMeta(stage: DraftStage): string {
    if (this.isTerminal(stage)) return "terminal · no clock";
    const n = stage.movesTo.length;
    return `no clock · ${n} move${n === 1 ? "" : "s"} out`;
  }

  canvasNote(stage: DraftStage): string {
    if (this.isTerminal(stage)) return "Terminal stage · closure reasons apply";
    return "No clock set";
  }

  transitionCount(): number {
    return this.stages.reduce((n, s) => n + s.movesTo.length, 0);
  }

  transitionLabel(): string {
    const n = this.transitionCount();
    return `${n} transition${n === 1 ? "" : "s"}`;
  }

  stageCountLabel(): string {
    const n = this.stages.length;
    return `${n} stage${n === 1 ? "" : "s"}`;
  }

  saveLabel(): string {
    if (this.saving) return "Saving…";
    return this.dirty ? "Save" : "Saved";
  }

  save(): void {
    if (!this.caseTypeKey || this.saving) {
      if (!this.caseTypeKey) this.hint = "Choose a case type before saving.";
      return;
    }
    const names = this.stages.map((s) => s.stage_name.trim());
    if (names.some((n) => !n)) {
      this.hint = "Every stage needs a name.";
      return;
    }
    const unique = new Set(names.map((n) => n.toLowerCase()));
    if (unique.size !== names.length) {
      this.hint = "Stage names must be unique for this case type.";
      return;
    }
    this.saving = true;
    this.hint = "";
    const payload = this.stages.map((s, sort_order) => ({
      stage_name: s.stage_name.trim(),
      sort_order,
      moves_to: this.moveNames(s),
    }));
    this.workflowService
      .saveCaseFlow(this.brand(), this.caseTypeKey, payload)
      .subscribe((ok) => {
        this.saving = false;
        if (ok) {
          this.dirty = false;
          this.hint = "";
        } else {
          this.hint =
            "Save failed. Run scripts/workflow-api/12_create_case_flow.sql and confirm the case-flow API is available.";
        }
      });
  }

  private blank(name: string): DraftStage {
    return {
      uid: this.nextUid++,
      stage_name: name,
      movesTo: [],
    };
  }

  private toDraft(stages: CaseStage[]): DraftStage[] {
    const rows = stages.map((s) => ({
      uid: this.nextUid++,
      stage_id: s.stage_id,
      stage_name: s.stage_name,
      movesTo: [] as number[],
    }));
    const byName = new Map(
      rows.map((r) => [r.stage_name.trim().toLowerCase(), r.uid])
    );
    const explicit = stages.some((s) => (s.moves_to?.length || 0) > 0);
    rows.forEach((row, i) => {
      const source = stages[i];
      if (explicit) {
        row.movesTo = (source.moves_to || [])
          .map((name) => byName.get(name.trim().toLowerCase()))
          .filter((id): id is number => id != null && id !== row.uid);
      } else if (i < rows.length - 1) {
        row.movesTo = [rows[i + 1].uid];
      }
    });
    return rows;
  }
}
